import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { formatWithOrdinal } from '../../../utils/dateHelpers';
import GreetingHeader from '../../shared/GreetingHeader';
import LoadingDots from '../../shared/LoadingDots';

const ReceptionistDashboard = ({ user, navigateTo }) => {
  const [newPatientsToday, setNewPatientsToday] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(true);
  const [loadingAppointments, setLoadingAppointments] = useState(true);

  useEffect(() => {
    // Fetch new patients for today
    fetch('/api/v1/patients/today')
      .then((res) => res.json())
      .then((data) => setNewPatientsToday(data.length))
      .catch((err) => {
        console.error('Failed to fetch new patients', err);
        setNewPatientsToday(0);
      })
      .finally(() => setLoadingPatients(false));

    // Fetch appointments
    fetch('/api/v1/appointments')
      .then((res) => res.json())
      .then((data) => setAppointments(data))
      .catch((err) => {
        console.error('Failed to fetch appointments', err);
        setAppointments([]);
      })
      .finally(() => setLoadingAppointments(false));
  }, []);

  // Filter upcoming appointments this month
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const upcomingAppointments = appointments.filter((appt) => {
    const date = new Date(appt.scheduled_at);
    return (
      date.getMonth() === currentMonth
      && date.getFullYear() === currentYear
      && (appt.status === 'pending' || appt.status === 'confirmed')
    );
  });

  return (
    <div className="p-6">
     <GreetingHeader name={user?.name} />

      <div className="labdashboard grid grid-cols-4 sm:grid-cols-2 gap-6">
        {/* New Patients added today */}
        <div className="pending col-span-1 col-start-1 mx-auto bg-white border rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold mb-2">Today&apos;s New Patients</h3>
          {loadingPatients ? (
            <LoadingDots height={10} width={30} color="#38AD3E" />
          ) : (
            <p className="text-3xl mx-auto font-bold text-blue-600"><strong>{newPatientsToday}</strong></p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="quick-actions pending mt-8 col-span-2 col-start-2">
          <h3 className="text-lg font-semibold mb-3 mx-auto">Quick Actions</h3>
          <div className="flex gap-[10px]">
            <button
              type="button"
              onClick={() => navigateTo('new_patients')}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-lg shadow"
            >
              + Add New Patient
            </button>
            <button
              type="button"
              onClick={() => navigateTo('all_patients')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg shadow"
            >
              View All Patients
            </button>

            <button
              type="button"
              onClick={() => navigateTo('patients_queue')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-lg shadow"
            >
              View patients queue
            </button>
          </div>
        </div>

        {/* Upcoming Appointments */}
        <div className="pending col-span-2 mt-10 mx-auto bg-white border rounded-xl shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4">📅 Upcoming Appointments (This Month)</h3>
          {loadingAppointments &&  <LoadingDots height={10} width={30} color="#38AD3E" />}

          {!loadingAppointments && upcomingAppointments.length === 0 && (
            <p>No upcoming appointments this month.</p>
          )}

          {!loadingAppointments && upcomingAppointments.length > 0 && (
            <ul className="divide-y">
              {upcomingAppointments.slice(0, 5).map((appt) => (
                <li key={appt.id} className="py-2">
                  <p>
                    <strong>{appt.patient?.name}</strong>
                    {' '}
                    –
                    {' '}
                    <span>{formatWithOrdinal(appt.scheduled_at)}</span>
                    {' '}
                    –
                    {' '}
                    <span className="capitalize">{appt.status}</span>
                  </p>
                </li>
              ))}
            </ul>
          )}

        </div>
      </div>
    </div>
  );
};

ReceptionistDashboard.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  navigateTo: PropTypes.func.isRequired,
};

export default ReceptionistDashboard;
